import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { InfoOutlined as InfoOutlinedIcon } from "@mui/icons-material";
import { useMemo } from "react";
import { DropdownSelect } from "../../../../components/dropdown-select";
import { HdgDatePicker } from "../../../../components/hdg-date-picker";
import {
  BATHROOM_OPTIONS,
  BEDROOM_OPTIONS,
  OWNERSHIP_DURATION_OPTIONS,
  PROPERTY_DETAILS_OPTIONS,
  PROJECT_PROPERTY_TYPE_OPTIONS,
  PROJECT_SCOPE_OPTIONS,
  PROJECT_SPECIAL_REQUIREMENT_OPTIONS,
  PROPERTY_LOCATION_OPTIONS,
  OTHER_REASON_FOR_HIRING,
  REASON_FOR_HIRING_OPTIONS,
  RESIDENCE_TYPE_OPTIONS,
  SQUARE_FOOTAGE_OPTIONS,
} from "../../data";
import {
  BILLING_METHODS,
  BUDGET_RANGES,
} from "../../../../components/homeowner-preferences-quiz/constants";
import {
  SECTION_BG,
  SECTION_RADIUS,
  inputSx,
  selectSx,
  selectWrapperSx,
} from "../../styles";
import { normalizeSingleSentence } from "../../utils";
import type { HomeownerProjectRecord } from "../../types";
import { ChipsField, FieldBlock, YesNoToggle } from "../shared/Fields";

const projectLabelSx = {
  color: "var(--black-100, #1C1C1C)",
  fontFeatureSettings: "'ss01' on, 'cv01' on, 'cv11' on",
  fontFamily: "Jost",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 600,
  lineHeight: "20px",
};

const projectInputSx = {
  ...inputSx,
  "& .MuiInputLabel-root": projectLabelSx,
};

const projectSelectWrapperSx = {
  ...selectWrapperSx,
  "& .MuiInputLabel-root": projectLabelSx,
};

type ProjectSectionProps = {
  hasMultipleProjects: boolean;
  activeProjects: HomeownerProjectRecord[];
  projectSelection: string;
  projectId: string | null;
  onProjectSelect: (projectId: string) => void;
  projectName: string;
  onProjectNameChange: (value: string) => void;
  projectTypeLabels: string[];
  projectScopeLabels: string[];
  projectStartDate: string | null;
  onProjectStartDateChange: (value: string | null) => void;
  billingPreferences: string[];
  budgetRange: string;
  hardDeadline: boolean;
  onHardDeadlineChange: (value: boolean) => void;
  deadlineDate: string | null;
  onDeadlineDateChange: (value: string | null) => void;
  isDeadlineDateValid: boolean;
  deadlineDateValidationText: string;
  financing: boolean;
  onFinancingChange: (value: boolean) => void;
  specialRequirements: string[];
  isEditable?: boolean;
  projectPropertyType?: string;
  onProjectPropertyTypeChange?: (value: string) => void;
  projectScope?: string[];
  onProjectScopeChange?: (value: string[]) => void;
  projectBillingPreferences?: string[];
  onProjectBillingPreferencesChange?: (value: string[]) => void;
  projectBudgetRange?: string;
  onProjectBudgetRangeChange?: (value: string) => void;
  projectSpecialRequirements?: string[];
  onProjectSpecialRequirementsChange?: (value: string[]) => void;
  reasonForHiring: string;
  onReasonForHiringChange: (value: string) => void;
  otherReasonForHiring: string;
  onOtherReasonForHiringChange: (value: string) => void;
  ownershipDuration: string;
  onOwnershipDurationChange: (value: string) => void;
  propertyDetails: string;
  onPropertyDetailsChange: (value: string) => void;
  showPropertyDetails: boolean;
  residenceType: string;
  onResidenceTypeChange: (value: string) => void;
  propertyLocation: string;
  onPropertyLocationChange: (value: string) => void;
  squareFootage: string;
  onSquareFootageChange: (value: string) => void;
  bedrooms: string;
  onBedroomsChange: (value: string) => void;
  bathrooms: string;
  onBathroomsChange: (value: string) => void;
};

const ProjectSection = ({
  hasMultipleProjects,
  activeProjects,
  projectSelection,
  projectId,
  onProjectSelect,
  projectName,
  onProjectNameChange,
  projectTypeLabels,
  projectScopeLabels,
  projectStartDate,
  onProjectStartDateChange,
  billingPreferences,
  budgetRange,
  hardDeadline,
  onHardDeadlineChange,
  deadlineDate,
  onDeadlineDateChange,
  isDeadlineDateValid,
  deadlineDateValidationText,
  financing,
  onFinancingChange,
  specialRequirements,
  isEditable = false,
  projectPropertyType = "",
  onProjectPropertyTypeChange,
  projectScope = [],
  onProjectScopeChange,
  projectBillingPreferences = [],
  onProjectBillingPreferencesChange,
  projectBudgetRange = "",
  onProjectBudgetRangeChange,
  projectSpecialRequirements = [],
  onProjectSpecialRequirementsChange,
  reasonForHiring,
  onReasonForHiringChange,
  otherReasonForHiring,
  onOtherReasonForHiringChange,
  ownershipDuration,
  onOwnershipDurationChange,
  propertyDetails,
  onPropertyDetailsChange,
  showPropertyDetails,
  residenceType,
  onResidenceTypeChange,
  propertyLocation,
  onPropertyLocationChange,
  squareFootage,
  onSquareFootageChange,
  bedrooms,
  onBedroomsChange,
  bathrooms,
  onBathroomsChange,
}: ProjectSectionProps) => {
  const selectedReasonOption = useMemo(
    () => REASON_FOR_HIRING_OPTIONS.find((option) => option.value === reasonForHiring),
    [reasonForHiring]
  );
  const getOptionLabel = (
    options: { value: string; label: string }[],
    value: string
  ) => options.find((option) => option.value === value)?.label ?? value;
  const renderMultiValue = (
    options: { value: string; label: string }[],
    values: string[]
  ) => values.map((value) => getOptionLabel(options, value)).join(", ");

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h5" fontWeight={700} sx={{ fontSize: "20px" }}>
          Project
        </Typography>
      </Box>
      {hasMultipleProjects && (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: "16px" }}>
            Change Project:
          </Typography>
          <FormControl size="small" sx={{ minWidth: 240, ...selectWrapperSx }}>
            <InputLabel>Project</InputLabel>
            <Select
              label="Project"
              value={projectSelection || projectId || ""}
              onChange={(event) => onProjectSelect(event.target.value as string)}
              sx={selectSx}
            >
              {activeProjects.map((project) => (
                <MenuItem
                  key={project._id ?? project.projectName ?? ""}
                  value={project._id ?? ""}
                >
                  {project.projectName || "Untitled Project"}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      )}

      <Box
      sx={{
        backgroundColor: SECTION_BG,
        borderRadius: SECTION_RADIUS,
        p: { xs: 2, md: 3 },
      }}
    >
        <Typography
          sx={{
            color: "#000",
            fontFamily: "var(--Font-Family-Titles, Jost)",
            fontSize: "var(--Font-Size-2XL, 25px)",
            fontStyle: "normal",
            fontWeight: 600,
            lineHeight: "var(--Font-Line-Height-LG, 40px)",
          }}
        >
          Project scope, logistics, timing, and special needs.
        </Typography>
        <Grid container spacing={3} mt={1}>
          <Grid item xs={12} md={4}>
            <FieldBlock title="Give your project a name." titleSx={projectLabelSx}>
              <TextField
                label="Project Name *"
                value={projectName}
                onChange={(event) => onProjectNameChange(event.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                fullWidth
                sx={projectInputSx}
              />
            </FieldBlock>
          </Grid>
          <Grid item xs={12} md={4}>
            {isEditable ? (
              <FieldBlock title="Property Type" titleSx={projectLabelSx}>
                <DropdownSelect
                  label="Property Type *"
                  value={projectPropertyType}
                  onChange={(value) => onProjectPropertyTypeChange?.(value)}
                  options={PROJECT_PROPERTY_TYPE_OPTIONS}
                  renderValue={(selected) =>
                    getOptionLabel(PROJECT_PROPERTY_TYPE_OPTIONS, String(selected))
                  }
                  size="small"
                  sx={selectSx}
                  wrapperSx={{ mb: 0, ...projectSelectWrapperSx }}
                />
              </FieldBlock>
            ) : (
              <ChipsField
                title="Property Type"
                titleSx={projectLabelSx}
                values={projectTypeLabels}
              />
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            <FieldBlock
              title="Please select your preferred project start date."
              titleSx={projectLabelSx}
            >
              <HdgDatePicker
                label="Start Date *"
                value={projectStartDate}
                onChange={onProjectStartDateChange}
                isValid
                textFieldSx={projectInputSx}
              />
            </FieldBlock>
          </Grid>

          <Grid item xs={12} md={4}>
            {isEditable ? (
              <FieldBlock title="Billing Preferences" titleSx={projectLabelSx}>
                <DropdownSelect<string[]>
                  label="Billing Preferences"
                  value={projectBillingPreferences}
                  onChange={(value) =>
                    onProjectBillingPreferencesChange?.(
                      Array.isArray(value) ? value : []
                    )
                  }
                  options={BILLING_METHODS}
                  multiple
                  renderValue={(selected) =>
                    renderMultiValue(
                      BILLING_METHODS,
                      Array.isArray(selected) ? (selected as string[]) : []
                    )
                  }
                  size="small"
                  sx={selectSx}
                  wrapperSx={{ mb: 0, ...projectSelectWrapperSx }}
                />
              </FieldBlock>
            ) : (
              <ChipsField
                title="Billing Preferences"
                titleSx={projectLabelSx}
                values={billingPreferences}
              />
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            {isEditable ? (
              <FieldBlock title="Budget Range" titleSx={projectLabelSx}>
                <DropdownSelect
                  label="Budget Range"
                  value={projectBudgetRange}
                  onChange={(value) => onProjectBudgetRangeChange?.(value)}
                  options={BUDGET_RANGES}
                  renderValue={(selected) =>
                    getOptionLabel(BUDGET_RANGES, String(selected))
                  }
                  size="small"
                  sx={selectSx}
                  wrapperSx={{ mb: 0, ...projectSelectWrapperSx }}
                />
              </FieldBlock>
            ) : (
              <ChipsField
                title="Budget Range"
                titleSx={projectLabelSx}
                values={budgetRange ? [budgetRange] : []}
              />
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            <FieldBlock title="Is this a hard deadline?" titleSx={projectLabelSx}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <YesNoToggle value={hardDeadline} onChange={onHardDeadlineChange} />
                {hardDeadline && (
                  <Box sx={{ flex: 1, minWidth: 160 }}>
                    <HdgDatePicker
                      label="Deadline Date *"
                      value={deadlineDate}
                      onChange={onDeadlineDateChange}
                      isValid={isDeadlineDateValid}
                      validationText={deadlineDateValidationText}
                      textFieldSx={projectInputSx}
                    />
                  </Box>
                )}
              </Stack>
            </FieldBlock>
          </Grid>

          <Grid item xs={12} md={4}>
            <FieldBlock
              title="Are you interested in financing?"
              titleSx={projectLabelSx}
            >
              <YesNoToggle value={financing} onChange={onFinancingChange} />
            </FieldBlock>
          </Grid>
          <Grid item xs={12} md={4}>
            {isEditable ? (
              <FieldBlock title="Project Type" titleSx={projectLabelSx}>
                <DropdownSelect<string[]>
                  label="Project Type"
                  value={projectScope}
                  onChange={(value) =>
                    onProjectScopeChange?.(Array.isArray(value) ? value : [])
                  }
                  options={PROJECT_SCOPE_OPTIONS}
                  multiple
                  renderValue={(selected) =>
                    renderMultiValue(
                      PROJECT_SCOPE_OPTIONS,
                      Array.isArray(selected) ? (selected as string[]) : []
                    )
                  }
                  size="small"
                  sx={selectSx}
                  wrapperSx={{ mb: 0, ...projectSelectWrapperSx }}
                />
              </FieldBlock>
            ) : (
              <ChipsField
                title="Project Type"
                titleSx={projectLabelSx}
                values={projectScopeLabels}
              />
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            {isEditable ? (
              <FieldBlock title="Special Requirements" titleSx={projectLabelSx}>
                <DropdownSelect<string[]>
                  label="Special Requirements"
                  value={projectSpecialRequirements}
                  onChange={(value) =>
                    onProjectSpecialRequirementsChange?.(
                      Array.isArray(value) ? value : []
                    )
                  }
                  options={PROJECT_SPECIAL_REQUIREMENT_OPTIONS}
                  multiple
                  renderValue={(selected) =>
                    renderMultiValue(
                      PROJECT_SPECIAL_REQUIREMENT_OPTIONS,
                      Array.isArray(selected) ? (selected as string[]) : []
                    )
                  }
                  size="small"
                  sx={selectSx}
                  wrapperSx={{ mb: 0, ...projectSelectWrapperSx }}
                />
              </FieldBlock>
            ) : (
              <ChipsField
                title="Special Requirements"
                titleSx={projectLabelSx}
                values={specialRequirements}
              />
            )}
          </Grid>

          <Grid item xs={12} md={4}>
            <FieldBlock
              title="Tell us the reason for hiring a designer?"
              titleSx={projectLabelSx}
            >
              <Stack spacing={1.5}>
                <FormControl size="small" sx={projectSelectWrapperSx} fullWidth>
                  <InputLabel>Motivation for hiring a designer *</InputLabel>
                  <Tooltip
                    title={selectedReasonOption?.tooltip ?? ""}
                    arrow
                    placement="top"
                    disableHoverListener={!selectedReasonOption?.tooltip}
                  >
                    <Select
                      label="Motivation for hiring a designer *"
                      value={reasonForHiring}
                      onChange={(event) => {
                        const value = event.target.value as string;
                        onReasonForHiringChange(value);
                        if (value !== OTHER_REASON_FOR_HIRING) {
                          onOtherReasonForHiringChange("");
                        }
                      }}
                      sx={selectSx}
                      renderValue={(selected) => (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Typography variant="body2" sx={{ fontSize: "13px" }}>
                            {String(selected)}
                          </Typography>
                          {selectedReasonOption?.tooltip ? (
                            <InfoOutlinedIcon fontSize="small" color="action" />
                          ) : null}
                        </Box>
                      )}
                    >
                      {REASON_FOR_HIRING_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.value}
                        </MenuItem>
                      ))}
                    </Select>
                  </Tooltip>
                </FormControl>
                {reasonForHiring === OTHER_REASON_FOR_HIRING && (
                  <TextField
                    label="Write in your answer"
                    value={otherReasonForHiring}
                    onChange={(event) =>
                      onOtherReasonForHiringChange(
                        normalizeSingleSentence(event.target.value)
                      )
                    }
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    helperText="One sentence only."
                    sx={projectInputSx}
                  />
                )}
              </Stack>
            </FieldBlock>
          </Grid>
          <Grid item xs={12} md={4}>
            <FieldBlock
              title="How long have you lived/owned the your property?"
              titleSx={projectLabelSx}
            >
              <DropdownSelect
                label="Residence Timeline *"
                value={ownershipDuration}
                onChange={onOwnershipDurationChange}
                options={OWNERSHIP_DURATION_OPTIONS}
                size="small"
                sx={selectSx}
                wrapperSx={{ mb: 0, ...projectSelectWrapperSx }}
              />
            </FieldBlock>
          </Grid>
          {showPropertyDetails && (
            <Grid item xs={12} md={4}>
              <FieldBlock title="Enter your property details:" titleSx={projectLabelSx}>
                <DropdownSelect
                  label="Property Type *"
                  value={propertyDetails}
                  onChange={onPropertyDetailsChange}
                  options={PROPERTY_DETAILS_OPTIONS}
                  size="small"
                  sx={selectSx}
                  wrapperSx={{ mb: 0, ...projectSelectWrapperSx }}
                />
              </FieldBlock>
            </Grid>
          )}

          <Grid item xs={12} md={4}>
            <FieldBlock
              title="What type of residence this is?"
              titleSx={projectLabelSx}
            >
              <DropdownSelect
                label="Residence Type *"
                value={residenceType}
                onChange={onResidenceTypeChange}
                options={RESIDENCE_TYPE_OPTIONS}
                size="small"
                sx={selectSx}
                wrapperSx={{ mb: 0, ...projectSelectWrapperSx }}
              />
            </FieldBlock>
          </Grid>
          <Grid item xs={12} md={4}>
            <FieldBlock title="Where is this property located?" titleSx={projectLabelSx}>
              <DropdownSelect
                label="Property Location *"
                value={propertyLocation}
                onChange={onPropertyLocationChange}
                options={PROPERTY_LOCATION_OPTIONS}
                size="small"
                sx={selectSx}
                wrapperSx={{ mb: 0, ...projectSelectWrapperSx }}
              />
            </FieldBlock>
          </Grid>
          <Grid item xs={12} md={4}>
            <FieldBlock
              title="What is the estimated project square footage?"
              titleSx={projectLabelSx}
            >
              <DropdownSelect
                label="Square Footage *"
                value={squareFootage}
                onChange={onSquareFootageChange}
                options={SQUARE_FOOTAGE_OPTIONS}
                size="small"
                sx={selectSx}
                wrapperSx={{ mb: 0, ...projectSelectWrapperSx }}
              />
            </FieldBlock>
          </Grid>

          <Grid item xs={12} md={4}>
            <FieldBlock title="How many bedrooms?" titleSx={projectLabelSx}>
              <DropdownSelect
                label="Bedrooms *"
                value={bedrooms}
                onChange={onBedroomsChange}
                options={BEDROOM_OPTIONS}
                size="small"
                sx={selectSx}
                wrapperSx={{ mb: 0, ...projectSelectWrapperSx }}
              />
            </FieldBlock>
          </Grid>
          <Grid item xs={12} md={4}>
            <FieldBlock title="How many bathrooms?" titleSx={projectLabelSx}>
              <DropdownSelect
                label="Bathrooms *"
                value={bathrooms}
                onChange={onBathroomsChange}
                options={BATHROOM_OPTIONS}
                size="small"
                sx={selectSx}
                wrapperSx={{ mb: 0, ...projectSelectWrapperSx }}
              />
            </FieldBlock>
          </Grid>
          <Grid item xs={12} md={4} />
        </Grid>
      </Box>
    </Stack>
  );
};

export default ProjectSection;
